import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { container } from "@/utils/mobx";
import { ContextMenuStore, ContextMenuOption } from "./store";
import "./s.context_menu.css";


export type { ContextMenuOption };

export const ContextMenu = container((props: {
    store: ContextMenuStore;
    options: ContextMenuOption[];
    isVisible?: boolean;
    onClose?: () => any;
    onSelect?: (value: string) => any;
    className?: string;
}) => {
    const store = props.store;

    return (
        <div className={"context_menu" + (store.className ? " " + store.className : "")}>
            <ContextMenuPopup store={store} />
        </div>
    );
}, ContextMenuStore);


const VIEWPORT_MARGIN = 4;

const ContextMenuPopup = observer((props: {
    store: ContextMenuStore;
}) => {
    const store = props.store;
    const ref = useRef<HTMLUListElement>(null);
    const [adjustedPos, setAdjustedPos] = useState<{ left: number; top: number } | null>(null);

    useEffect(() => {
        function keyHandler(e: KeyboardEvent) {
            if (!store.isVisible) return;
            store.keyDown(e);
            e.stopPropagation();
        }
        document.addEventListener("keydown", keyHandler, false);
        return () => {
            document.removeEventListener("keydown", keyHandler);
        };
    }, []);

    // Runs synchronously after DOM mutation, before browser paint — no visible flash.
    useLayoutEffect(() => {
        if (!store.isVisible || store.x === null || store.y === null) {
            setAdjustedPos(null);
            return;
        }
        const el = ref.current;
        if (!el) return;
        const { width, height } = el.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        setAdjustedPos({
            left: Math.max(VIEWPORT_MARGIN, Math.min(store.x, vw - width - VIEWPORT_MARGIN)),
            top:  Math.max(VIEWPORT_MARGIN, Math.min(store.y, vh - height - VIEWPORT_MARGIN)),
        });
    // store.options in deps so we re-measure if items change while visible
    }, [store.isVisible, store.x, store.y, store.options]);

    const hasCoords = store.x !== null && store.y !== null;
    const style: React.CSSProperties = {
        ...(hasCoords ? {
            position: "fixed",
            left: adjustedPos?.left ?? store.x!,
            top:  adjustedPos?.top  ?? store.y!,
            minWidth: "auto",
            // hide on first render pass while measuring to avoid position flash
            ...(!adjustedPos ? { visibility: "hidden" } : {}),
        } : {}),
        ...(!store.isVisible ? { opacity: 0, pointerEvents: "none" } : {}),
    };

    return (
        <>
        {store.isVisible && (
            <div
                className="context_menu__backdrop"
                onMouseDown={() => setTimeout(() => store.close(), 300)}
                onTouchStart={() => setTimeout(() => store.close(), 300)}
            />
        )}
        <ul className="context_menu__popup" ref={ref} style={style}>
            {(store.options || []).map((option, i) => (
                <li
                    key={option.value}
                    className={
                        "context_menu__option" +
                        (option.isDisabled ? " disabled" : "") +
                        (store.focusedOptionIndex === i ? " focused" : "")
                    }
                    onClick={() => store.selectOption(i)}
                    onMouseEnter={() => store.focusOption(i)}
                >
                    {option.title}
                </li>
            ))}
        </ul>
        </>
    );
});
